import { ICellRendererAngularComp } from 'ag-grid-angular';
import { IRowNode } from 'ag-grid-community';

import { Component } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CapacityStatus } from '@core/enums/capacity-status.enum';
import { ProgressBarStatus } from '@core/enums/progress-bar-status.enum';
import { PlanningBoardSettings } from '@core/models/planning-board-settings';
import { PeriodRendererParams } from '@core/typings/period-renderer.params';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateModule } from '@ngx-translate/core';
import { Period } from '@portal-shared/app/entities/period.model';
import { FormatNumberPipe } from '@portal-shared/app/pipes/format-number.pipe';
import { PeriodUsageDto } from '@services/dtos/rows/period-usage.dto';
import { ResourceLikeDto } from '@services/dtos/rows/resource-like.dto';
import { CellDropZoneComponent } from '@shared/components/cell-drop-zone/cell-drop-zone.component';
import { ProgressBarComponent } from '@shared/components/progress-bar/progress-bar.component';
import { PeriodCapacityHelper } from '@shared/helpers/period-capacity.helper';
import { ResourceGroupRow } from '../../rows/groups/resource-group.row';
import { PeriodSumsComponent } from '../period-sums/period-sums.component';

@Component({
    selector: 'al-period-capacity-cell-renderer',
    templateUrl: './period-capacity-cell-renderer.component.html',
    styleUrls: ['./period-capacity-cell-renderer.component.scss'],
    imports: [
        CellDropZoneComponent,
        ProgressBarComponent,
        FontAwesomeModule,
        PeriodSumsComponent,
        MatTooltipModule,
        TranslateModule,
        FormatNumberPipe
    ]
})
export class PeriodCapacityCellRendererComponent implements ICellRendererAngularComp {
  public planningSettings?: PlanningBoardSettings;

  public capacity = 0;
  public plannedHours = 0;
  public percentProgress = 0;
  public status = ProgressBarStatus.Neutral;
  public isOverloaded?: boolean;

  public isLoading = true;
  public resource?: ResourceLikeDto;
  public period?: Period;
  public node?: IRowNode<ResourceGroupRow>;

  public agInit(params: PeriodRendererParams<ResourceGroupRow, PeriodUsageDto>) {
    this.update(params);
  }

  public refresh(params: PeriodRendererParams<ResourceGroupRow, PeriodUsageDto>): boolean {
    this.update(params);
    return true;
  }

  private update(params: PeriodRendererParams<ResourceGroupRow, PeriodUsageDto>) {
    if (!params.value) return;

    this.isLoading = false;
    this.planningSettings = params.planningBoardSettings;
    this.period = params.period;
    this.node = params.node;

    const { plannedHours, capacity } = params.value;
    this.capacity = capacity ?? this.node.data?.dto.capacity ?? 0;
    this.plannedHours = plannedHours ?? 0;

    this.computeProgress(this.planningSettings);
  }

  public computeProgress(planningSettings: PlanningBoardSettings) {
    const rawPercentage = PeriodCapacityHelper.computeCapacityPercentage(
      planningSettings,
      this.plannedHours,
      this.capacity
    );

    this.percentProgress = Math.min(100, rawPercentage);
    const periodStatus = PeriodCapacityHelper.computeStatus(planningSettings, rawPercentage, this.capacity);
    this.isOverloaded = periodStatus === CapacityStatus.Overloaded;
    this.status = PeriodCapacityHelper.toProgressBarStatus(periodStatus);
  }

  public expand(expand = true) {
    if (!this.node?.group) return;

    this.node?.setExpanded(expand);
  }
  public toggleExpand() {
    this.expand(!this.node?.expanded);
  }
}
